import { ICategoryWithProducts } from '@/features/brand-menu';
import type { MouseEvent } from 'react';
import { useCustomLocale } from '@/shared/hooks/use-custom-locale';
import { Avatar } from '@/shared/ui/avatar';
import { Button } from '@/shared/ui/button';
import { Image, Stack, Text } from '@chakra-ui/react';
import { useLocale } from 'next-intl';
import {
  categoryHref,
  isModifiedClick,
  rememberCategoryHash,
} from '../../../category-menu-link';

// Just in case
const showImage = false;

function scrollToCategory(slug: string) {
  const element = document.getElementById(slug);
  if (!element) return;

  const elementPosition =
    element.getBoundingClientRect().top + window.pageYOffset;
  window.scrollTo({
    top: elementPosition,
    behavior: 'smooth',
  });
  rememberCategoryHash(slug);
}

export default function Mobile({
  category,
  active,
}: {
  category: ICategoryWithProducts;
  active: boolean;
}) {
  const currentLocale = useCustomLocale();
  const locale = useLocale();
  const href = categoryHref(locale, category.slug);

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (isModifiedClick(event)) return;
    event.preventDefault();
    scrollToCategory(category.slug);
  };

  if (showImage) {
    return (
      <Stack mr={4} gap={2} minW="56px" asChild>
        <a href={href} onClick={onClick}>
          {category?.image ? (
            <Image
              width="56px"
              height="56px"
              style={{ borderRadius: '16px' }}
              src={`${process.env.BASE_URL}/${category?.image}`}
              alt={category?.title[currentLocale]}
            />
          ) : (
            <Avatar
              width="56px"
              height="56px"
              borderRadius="16px"
              name={category?.title[currentLocale]}
              bg="var(--bg-default)"
              color="text"
            />
          )}
          <Text
            whiteSpace="nowrap"
            textAlign="center"
            fontSize="xs"
            lineClamp="2"
          >
            {category?.title[currentLocale]}
          </Text>
        </a>
      </Stack>
    );
  }

  return (
    <Button
      rounded="full"
      size="sm"
      fontWeight={500}
      {...(active
        ? {
            bg: 'primary',
            color: 'primary.text',
            _hover: {
              bg: 'primary.hover',
            },
          }
        : {
            variant: 'subtle',
          })}
      asChild
    >
      <a href={href} onClick={onClick}>
        {category.title?.[currentLocale]}
      </a>
    </Button>
  );
}
